import Helpers from "Modules/Helpers";
import { safeGetItem, safeSetItem } from "Utils/safeLocalStorage";
import api from "../../api";

import {
  deleteCommentInList,
  getDonationFromLocalStorage,
  removeDonationFromLocalStorage,
  sortCommentsList,
  triggerFirstCommentNotice,
  triggerFirstImageNotice,
  updateCommentInList,
} from "../../helpers";

const LOCALSTORAGE_CREATED_COMMENTS_KEY = "memorialPageComments";
const LOCALSTORAGE_LIKED_COMMENTS_KEY = "obituaryLikedComments";

const initialState = {
  commentsList: [],
  createdComments: [],
  files: [],
  imagesList: [],
  isActionInModal: false,
  isLoading: false,
  isLoadingImages: false,
  likedComments: [],
  localStorageCreatedCommentsKey: LOCALSTORAGE_CREATED_COMMENTS_KEY,
  localStorageLikedCommentsKey: LOCALSTORAGE_LIKED_COMMENTS_KEY,
  newCommentId: null,
  preventRelayoutingMasonry: false,
  uploaded: false,
  uploading: false,
};

const getters = {
  getActiveComments: ({ commentsList, createdComments }) =>
    commentsList.filter(({ hasPendingMedia, id }) => {
      if (!hasPendingMedia) {
        return true;
      }

      if (!createdComments) {
        return false;
      }

      const canEdit = createdComments.find((comment) => comment.id === id);

      return !!canEdit;
    }),
};

const mutations = {
  changeLikeCountInComment(_state, { comment, changes }) {
    const finalComment = {
      ...comment,
      likes: comment.likes + changes,
    };

    if (comment.parentId) {
      const parent = _state.commentsList.find(
        ({ id }) => id === comment.parentId
      );
      parent.children = updateCommentInList(finalComment, parent.children);
      _state.commentsList = updateCommentInList(parent, _state.commentsList);
    } else {
      _state.commentsList = updateCommentInList(
        finalComment,
        _state.commentsList
      );
    }
  },

  initialiseCreatedCommentsStore(_state) {
    const item = safeGetItem(LOCALSTORAGE_CREATED_COMMENTS_KEY);
    if (item) {
      _state.createdComments = JSON.parse(item);
    }
  },

  initialiseLikedCommentsStore(_state) {
    const item = safeGetItem(LOCALSTORAGE_LIKED_COMMENTS_KEY);
    if (item) {
      _state.likedComments = JSON.parse(item);
    }
  },

  resetFiles(_state) {
    _state.files = [];
  },

  setComment(_state, { commentId, data }) {
    const comments = [..._state.commentsList];
    const index = comments.findIndex(({ id }) => id === commentId);

    if (index > -1) {
      comments.splice(index, 1, data);
    }

    _state.commentsList = comments;
  },

  setCommentsList(_state, data) {
    _state.commentsList = data;
  },

  setCreatedComments(_state, comment) {
    _state.createdComments = comment;
  },

  setFiles(_state, files = []) {
    _state.files = [..._state.files, ...files];
  },

  setImagesList(_state, data) {
    _state.imagesList = data;
  },

  setIsActionInModal(_state, status) {
    _state.isActionInModal = status;
  },
  setIsLoading(_state, isLoading = true) {
    _state.isLoading = isLoading;
  },

  setIsLoadingImages(_state, isLoadingImages = true) {
    _state.isLoadingImages = isLoadingImages;
  },

  setLikedComments(_state, comments) {
    _state.likedComments = comments.splice(0);
  },

  setNewCommentId(_state, commentId) {
    _state.newCommentId = commentId;
  },

  setPreventRelayoutingMasonry(_state, status) {
    _state.preventRelayoutingMasonry = status;
  },

  uploadFailed(_state) {
    _state.commentsList.shift();
  },

  uploadSuccess(_state) {
    _state.uploading = false;
    _state.uploaded = true;
  },
};

const actions = {
  addCommentToStorage({ commit, state }, comment) {
    commit(
      "setCreatedComments",
      state.createdComments.length
        ? [comment, ...state.createdComments]
        : [comment]
    );
  },
  createObituaryComment({ dispatch, commit, state }, data = {}) {
    if (!data.parentId) {
      commit("setIsLoading");
    }

    const donation = getDonationFromLocalStorage();

    // Pull out the share-modal context fields so they don't get sent to the
    // REST endpoint as part of the comment payload.
    const {
      deceasedName: shareDeceasedName,
      possessiveName: sharePossessiveName,
      memorialFund: shareMemorialFund,
      ...payload
    } = data;

    return new Promise((resolve, reject) => {
      let endpoint = "create-obituary-comment";
      const hasFiles = payload.files?.length;

      if (donation && !payload.parentId) {
        endpoint = "update-obituary-comment";
        payload.hash = donation.hash;
        payload.id = donation.id;
      }

      api
        .post(endpoint, payload)
        // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Legacy
        .then(async (comment) => {
          commit("uploadSuccess");

          dispatch("addCommentToStorage", {
            hash: comment.hash,
            id: comment.id,
          });

          if (hasFiles) {
            await dispatch("getObituaryImages", data.obituaryId);
          }

          if (comment.parentId) {
            dispatch("updateThreadedCommentParent", comment.parentId);
          } else {
            commit("setIsLoading", false);

            const newList = [comment, ...state.commentsList];
            removeDonationFromLocalStorage();
            triggerFirstCommentNotice(newList, comment);

            if (hasFiles) {
              triggerFirstImageNotice(state.imagesList, comment);
            }

            commit("setCommentsList", newList);

            // When the comment was created with files, transloadit may still
            // be processing them so the returned comment has hasPendingMedia
            // set and an empty mediaImages array. Re-fetch after a short
            // delay so views that derive from commentsList (notably the
            // timeline) get the populated mediaImages without needing the
            // user to reload the page or switch to masonry view.
            if (hasFiles && comment.hasPendingMedia) {
              setTimeout(
                () => dispatch("getObituaryComment", comment.id),
                5000
              );
            }

            // Open the share modal for the freshly created top-level comment
            // immediately, so it works on every comments view (timeline +
            // masonry).
            // We also mark the hash in localStorage so SingleComment.setIsNew
            // skips re-opening when masonry mounts (or re-mounts after a tab
            // switch). The in-store openedNewCommentId guard covers the
            // session, the localStorage marker covers later page loads.
            if (comment.hash) {
              safeSetItem(`new-comment-${comment.hash}`, "true");
            }

            dispatch(
              "singleCommentModal/open",
              {
                comment,
                context: "share",
                deceasedName: shareDeceasedName || "",
                hash: comment.hash || "",
                isNew: true,
                memorialFund: shareMemorialFund || null,
                possessiveName: sharePossessiveName || "",
              },
              { root: true }
            );
          }

          return resolve(comment);
        })
        .catch((error) => {
          commit("uploadFailed");
          removeDonationFromLocalStorage();

          return reject(error);
        });
    });
  },

  deleteCommentInStorage({ commit, state }, comment) {
    commit(
      "setCreatedComments",
      state.createdComments.length
        ? deleteCommentInList(comment.id, state.createdComments)
        : []
    );
  },

  deleteObituaryComment({ dispatch, commit, state }, data) {
    if (!data.parentId) {
      commit("setIsLoading");
    }
    commit("setIsActionInModal", true);

    return new Promise((resolve, reject) => {
      api
        .delete(`delete-obituary-comment/${data.id}/${data.hash}`)
        .then(() => {
          commit("setIsActionInModal", false);

          dispatch("deleteCommentInStorage", data);

          if (data.parentId) {
            dispatch("updateThreadedCommentParent", data.parentId);
          } else {
            commit("setIsLoading", false);
            commit(
              "setCommentsList",
              deleteCommentInList(data.id, state.commentsList)
            );
          }

          return resolve();
        })
        .catch((error) => {
          commit("setIsLoading", false);
          commit("setIsActionInModal", false);
          commit("uploadFailed");
          return reject(error);
        });
    });
  },

  getObituaryComment({ commit }, commentId) {
    return new Promise((resolve, reject) => {
      api
        .get(`get-obituary-comment/${commentId}`)
        .then((data) => {
          commit("setComment", { commentId, data });

          return resolve();
        })
        .catch((error) => reject(error));
    });
  },

  getObituaryComments({ commit }, obituaryId) {
    commit("setIsLoading");

    return new Promise((resolve, reject) => {
      api
        .get(`get-obituary-comments/${obituaryId}`)
        .then((data) => {
          commit("setIsLoading", false);
          commit(
            "setCommentsList",
            sortCommentsList(data, LOCALSTORAGE_CREATED_COMMENTS_KEY)
          );

          return resolve();
        })
        .catch((error) => reject(error));
    });
  },

  getObituaryImages({ commit }, obituaryId) {
    commit("setIsLoadingImages");

    return new Promise((resolve, reject) => {
      api
        .get(`comments/get-uploaded-images/${obituaryId}`)
        .then((data) => {
          commit("setIsLoadingImages", false);
          commit("setImagesList", data);
          return resolve();
        })
        .catch((error) => reject(error));
    });
  },

  likeComment({ commit, state }, comment) {
    if (!comment.parentId && comment.likes !== 0) {
      commit("setPreventRelayoutingMasonry", true);
    }

    commit("changeLikeCountInComment", { changes: 1, comment });
    commit("setLikedComments", [{ id: comment.id }, ...state.likedComments]);

    return new Promise((resolve, reject) => {
      api
        .post(`like/${comment.id}`)
        .then((responseComment) => {
          const savingData = {
            id: responseComment.id,
          };

          commit(
            "setLikedComments",
            updateCommentInList(savingData, state.likedComments)
          );

          if (comment.parentId) {
            Helpers.gtmObituaryEvent("threadedLike");
          } else {
            Helpers.gtmObituaryEvent("like");
          }

          return resolve(comment);
        })
        .catch((error) => {
          commit(
            "setLikedComments",
            deleteCommentInList(comment.id, state.likedComments)
          );
          commit("changeLikeCountInComment", { changes: 0, comment });
          return reject(error);
        })
        .then(() => {
          if (!comment.parentId && comment.likes !== 0) {
            commit("setPreventRelayoutingMasonry", false);
          }
        });
    });
  },

  removeFile({ commit, state }, file) {
    const files = state.files.filter((f) => f.fileName !== file.name);
    commit("resetFiles");
    commit("setFiles", files);
  },

  reportObituaryComment(_context, data) {
    return api.post("report-obituary-comment", { id: data.id });
  },

  resetFiles({ commit }) {
    commit("resetFiles");
  },

  setFiles({ commit }, files = []) {
    commit("setFiles", files);
  },

  unlikeComment({ commit, state }, { comment, likedInfo }) {
    if (!comment.parentId && comment.likes !== 1) {
      commit("setPreventRelayoutingMasonry", true);
    }
    commit("changeLikeCountInComment", { changes: -1, comment });
    commit(
      "setLikedComments",
      deleteCommentInList(comment.id, state.likedComments)
    );

    return new Promise((resolve, reject) => {
      api
        .post(`unlike/${comment.id}`, {
          hash: likedInfo.hash,
          mid: likedInfo.mid,
        })
        .then((_responseComment) => resolve(comment))
        .catch((error) => {
          commit("changeLikeCountInComment", { changes: 0, comment });
          commit("setLikedComments", [likedInfo, ...state.likedComments]);

          return reject(error);
        })
        .then(() => {
          if (!comment.parentId && comment.likes !== 1) {
            commit("setPreventRelayoutingMasonry", false);
          }
        });
    });
  },

  updateCommentInStorage({ commit, state }, comment) {
    commit(
      "setCreatedComments",
      state.createdComments.length
        ? updateCommentInList(comment, state.createdComments)
        : [comment]
    );
  },

  updateNewCommentId({ commit }, id) {
    commit("setNewCommentId", id);
  },

  updateObituaryComment({ dispatch, commit, state }, data = {}) {
    if (!data.parentId) {
      commit("setIsLoading");
    }
    commit("setIsActionInModal", true);

    return new Promise((resolve, reject) => {
      api
        .post("update-obituary-comment", data)
        .then((comment) => {
          commit("setIsActionInModal", false);

          dispatch("updateCommentInStorage", {
            hash: comment.hash,
            id: comment.id,
          });

          if (data.parentId) {
            dispatch("updateThreadedCommentParent", data.parentId);
          } else {
            commit("setIsLoading", false);
            commit(
              "setCommentsList",
              updateCommentInList(comment, state.commentsList)
            );
          }

          return resolve(comment);
        })
        .catch((error) => {
          commit("setIsLoading", false);
          commit("setIsActionInModal", false);
          commit("uploadFailed");
          return reject(error);
        });
    });
  },

  updateThreadedCommentParent({ commit, state }, parentId) {
    commit("setPreventRelayoutingMasonry", true);

    api.get(`get-obituary-comment/${parentId}`).then((comment) => {
      commit(
        "setCommentsList",
        updateCommentInList(comment, state.commentsList)
      );
      commit("threadedComments/setParent", comment, { root: true });
    });

    commit("setPreventRelayoutingMasonry", false);
  },
};

export default {
  actions,
  getters,
  mutations,
  namespaced: true,
  state: initialState,
};
